import { z } from "zod/v4";

import { JSONValueSchema } from "../common";
import { BaseNodeExecutionResultSchema } from "../node-execution-result";

import {
  BaseNodeDefinition,
  BaseNodeDefinitionConfigSchema,
  BaseNodeDefinitionSchema,
} from "./base-node";

// Operation schemas
export const SetVariableOperationSchema = BaseNodeDefinitionConfigSchema.extend(
  {
    operation: z.literal("set"),
    variablePath: z.string(),
    variableValue: JSONValueSchema,
  }
);

export const SetVariableOperationResultSchema = z.object({
  operation: z.literal("set-variable"),
  variablePath: z.string(),
  value: JSONValueSchema,
  exists: z.boolean(),
});

export const GetVariableOperationSchema = BaseNodeDefinitionConfigSchema.extend(
  {
    operation: z.literal("get"),
    variablePath: z.string(),
    defaultValue: JSONValueSchema.optional(),
  }
);

export const GetVariableOperationResultSchema = z.object({
  operation: z.literal("get-variable"),
  variablePath: z.string(),
  value: JSONValueSchema,
  exists: z.boolean(),
});

export const IncrementVariableOperationSchema =
  BaseNodeDefinitionConfigSchema.extend({
    operation: z.literal("increment"),
    variablePath: z.string(),
    incrementBy: z.number(),
  });

export const IncrementVariableOperationResultSchema = z.object({
  operation: z.literal("increment-variable"),
  variablePath: z.string(),
  value: z.number(),
  exists: z.boolean(),
});

export const DecrementVariableOperationSchema =
  BaseNodeDefinitionConfigSchema.extend({
    operation: z.literal("decrement"),
    variablePath: z.string(),
    decrementBy: z.number(),
  });

export const DecrementVariableOperationResultSchema = z.object({
  operation: z.literal("decrement-variable"),
  variablePath: z.string(),
  value: z.number(),
  exists: z.boolean(),
});

export const DeleteVariableOperationSchema =
  BaseNodeDefinitionConfigSchema.extend({
    operation: z.literal("delete"),
    variablePath: z.string(),
  });

export const DeleteVariableOperationResultSchema = z.object({
  operation: z.literal("delete-variable"),
  variablePath: z.string(),
  exists: z.boolean(),
});

export const AppendVariableOperationSchema =
  BaseNodeDefinitionConfigSchema.extend({
    operation: z.literal("append"),
    variablePath: z.string(),
    valueToAppend: JSONValueSchema,
  });

export const AppendValueOperationResultSchema = z.object({
  operation: z.literal("append-variable"),
  variablePath: z.string(),
  value: z.array(JSONValueSchema),
  exists: z.boolean(),
});

export const MathVariableOperationSchema =
  BaseNodeDefinitionConfigSchema.extend({
    operation: z.literal("math"),
    variablePath: z.string(),
    expression: z.string(),
  });

export const MathVariableOperationResultSchema = z.object({
  operation: z.literal("math-variable"),
  variablePath: z.string(),
  value: z.number(),
  exists: z.boolean(),
});

// Union schemas
export const VariableNodeConfigSchema = z.discriminatedUnion("operation", [
  SetVariableOperationSchema,
  GetVariableOperationSchema,
  IncrementVariableOperationSchema,
  DecrementVariableOperationSchema,
  DeleteVariableOperationSchema,
  AppendVariableOperationSchema,
  MathVariableOperationSchema,
]);

export const VariableOperationResultSchema = z.discriminatedUnion("operation", [
  SetVariableOperationResultSchema,
  GetVariableOperationResultSchema,
  IncrementVariableOperationResultSchema,
  DecrementVariableOperationResultSchema,
  DeleteVariableOperationResultSchema,
  AppendValueOperationResultSchema,
  MathVariableOperationResultSchema,
]);

export const VariableNodeDefinitionSchema = BaseNodeDefinitionSchema.extend({
  type: z.literal("variable"),
  config: VariableNodeConfigSchema,
});

export const VariableNodeResultSchema = BaseNodeExecutionResultSchema.extend({
  type: z.literal("variable"),
});

// TypeScript types inferred from Zod schemas
export type VariableNodeDefinition = z.infer<
  typeof VariableNodeDefinitionSchema
>;
export type SetVariableOperation = z.infer<typeof SetVariableOperationSchema>;
export type SetVariableOperationResult = z.infer<
  typeof SetVariableOperationResultSchema
>;
export type GetVariableOperation = z.infer<typeof GetVariableOperationSchema>;
export type GetVariableOperationResult = z.infer<
  typeof GetVariableOperationResultSchema
>;
export type IncrementVariableOperation = z.infer<
  typeof IncrementVariableOperationSchema
>;
export type IncrementVariableOperationResult = z.infer<
  typeof IncrementVariableOperationResultSchema
>;
export type DecrementVariableOperation = z.infer<
  typeof DecrementVariableOperationSchema
>;
export type DecrementVariableOperationResult = z.infer<
  typeof DecrementVariableOperationResultSchema
>;
export type DeleteVariableOperation = z.infer<
  typeof DeleteVariableOperationSchema
>;
export type DeleteVariableOperationResult = z.infer<
  typeof DeleteVariableOperationResultSchema
>;
export type AppendVariableOperation = z.infer<
  typeof AppendVariableOperationSchema
>;
export type AppendValueOperationResult = z.infer<
  typeof AppendValueOperationResultSchema
>;
export type MathVariableOperation = z.infer<typeof MathVariableOperationSchema>;
export type MathVariableOperationResult = z.infer<
  typeof MathVariableOperationResultSchema
>;
export type VariableNodeConfig = z.infer<typeof VariableNodeConfigSchema>;
export type VariableOperationResult = z.infer<
  typeof VariableOperationResultSchema
>;
export type VariableNodeResult = z.infer<typeof VariableNodeResultSchema>;
